import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';

/**
 * Map of multiplier thresholds to their corresponding image and scale.
 * The keys represent the upper bound of the multiplier percentage.
 * For example:
 * - 100 means up to 100% (1.0 multiplier) will use the red image.
 * - 150 means above 100% and up to 150% (1.5 multiplier) will use the green image.
 * - 200 means above 150% and up to 200% (2.0 multiplier) will use the yellow image.
 */
const tierConfig = [
  {
    percent: 100,
    image: 'assets/ui/buddies/common/multiplier_red.png',
    scale: 1,
  },
  {
    percent: 150,
    image: 'assets/ui/buddies/common/multiplier_green.png',
    scale: 1.2,
  },
  {
    percent: 200,
    image: 'assets/ui/buddies/common/multiplier_yellow.png',
    scale: 1.4,
  },
] as const;

export class BuddiesMultiplierMark extends View {
  private base: ImageScaleView;
  private text: LangBitmapFontTextShadowView;
  private percent: LangBitmapFontTextShadowView;

  constructor(opts: ViewOpts) {
    super({
      ...opts,
      width: 80,
      height: 80,
      centerOnOrigin: true,
    });

    this.base = new ImageScaleView({
      superview: this,
      image: 'assets/ui/buddies/common/multiplier_red.png',
      scaleMethod: 'contain',
      width: this.style.width,
      height: this.style.height,
      x: this.style.width / 2,
      y: this.style.height / 2 + 3,
      centerOnOrigin: true,
    });

    this.text = new LangBitmapFontTextShadowView({
      superview: this.base,
      align: 'center',
      verticalAlign: 'center',
      size: 30,
      font: 'Body',
      color: '#FFFFFF',
      width: this.style.width - 12,
      height: this.style.height - 12,
      letterSpacing: -4,
      leading: -20,
      centerOnOrigin: true,
      x: this.base.style.width / 2 - 3,
      y: this.base.style.height / 2 - 8,
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      textShadowOpacity: 0.4,
    });

    this.percent = new LangBitmapFontTextShadowView({
      superview: this.base,
      align: 'center',
      verticalAlign: 'center',
      size: 18,
      font: 'Body',
      color: '#FFFFFF',
      width: this.text.style.width,
      height: this.text.style.height,
      letterSpacing: -4,
      leading: -20,
      centerOnOrigin: true,
      x: this.text.style.x,
      y: this.text.style.y + 23,
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      textShadowOpacity: 0.4,
      localeText: () => '%',
    });
  }

  public setMultiplier(multiplier: number) {
    // Use multiplier comparisons for tiering to avoid percent rounding issues
    // (e.g. `2` represented as `1.999999999` accidentally selecting the 150% tier).
    const multPercent = Math.round(multiplier * 100);

    const tier =
      tierConfig.find((entry) => multiplier <= entry.percent / 100) ??
      tierConfig[tierConfig.length - 1];

    this.base.updateOpts({
      image: tier.image,
      scale: tier.scale,
      x: this.style.width / 2,
      y: this.style.height / 2 + 3,
    });

    this.text.text = `${multPercent}`;
  }
}
